import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
	AppShell,
	Button,
	Card,
	Label,
	Reveal,
	Skeleton,
	StatTile,
	StatusPill,
	tokens,
	hairline,
} from "@vh/shared-ui";
import type { AccentName } from "@vh/shared-ui";
import { api, ApiClientError } from "../api/client";
import type { AdminOverview, HealthReport, HealthStatus } from "../api/types";
import { useAuth } from "../auth/AuthContext";

// Loader outcomes drive the four screen states plus the admin only case.
type LoadState =
	| { kind: "loading" }
	| { kind: "error"; message: string }
	| { kind: "forbidden" }
	| { kind: "ready"; overview: AdminOverview; health: HealthReport };

// Health maps to the accent that names its state: green up, gold degraded, red down.
function healthAccent(status: HealthStatus): AccentName {
	switch (status) {
		case "UP":
			return "green";
		case "DOWN":
		case "OUT_OF_SERVICE":
			return "red";
		default:
			return "gold";
	}
}

export function DashboardPage() {
	const { role, logout, markAnonymous } = useAuth();
	const [state, setState] = useState<LoadState>({ kind: "loading" });
	const [redirect, setRedirect] = useState(false);

	// Fetches the admin overview and the public health roll up together. A 401 demotes the session and
	// routes to login; a 403 is a signed in non admin; anything else is a retryable error.
	const load = useCallback(async () => {
		setState({ kind: "loading" });
		try {
			const [overview, health] = await Promise.all([api.overview(), api.health()]);
			setState({ kind: "ready", overview, health });
		} catch (err) {
			if (err instanceof ApiClientError) {
				if (err.status === 401) {
					markAnonymous();
					setRedirect(true);
					return;
				}
				if (err.status === 403) {
					setState({ kind: "forbidden" });
					return;
				}
				setState({ kind: "error", message: err.message });
				return;
			}
			setState({ kind: "error", message: "Something went wrong." });
		}
	}, [markAnonymous]);

	useEffect(() => {
		void load();
	}, [load]);

	if (redirect) return <Navigate to="/login" replace />;

	const actions = (
		<>
			{role && <Label tone="secondary">{role}</Label>}
			<Button variant="ghost" onClick={() => void logout()}>
				Sign out
			</Button>
		</>
	);

	return (
		<AppShell title="Maintenance Monkey" actions={actions}>
			{state.kind === "loading" && <LoadingState />}
			{state.kind === "error" && <ErrorState message={state.message} onRetry={() => void load()} />}
			{state.kind === "forbidden" && <ForbiddenState />}
			{state.kind === "ready" && <ReadyState overview={state.overview} health={state.health} />}
		</AppShell>
	);
}

// Loading is a skeleton in the shape of the content, never a spinner: a health line, two tiles, a list.
function LoadingState() {
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xl }}>
			<Card title="Service health">
				<Skeleton height={tokens.size.title} radius={tokens.radius.pill} width="40%" />
			</Card>
			<div style={gridStyle}>
				<TileSkeleton />
				<TileSkeleton />
			</div>
			<Card title="Modules">
				<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
					<Skeleton />
					<Skeleton width="70%" />
					<Skeleton width="85%" />
				</div>
			</Card>
		</div>
	);
}

// A stat tile placeholder in the tile's own chrome: label line above the value line.
function TileSkeleton() {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: tokens.space.sm,
				border: hairline(tokens.color.stroke.hairline),
				borderRadius: tokens.radius.lg,
				padding: tokens.space.lg,
			}}
		>
			<Skeleton height={tokens.size.micro} width="45%" />
			<Skeleton height={tokens.size.display} width="60%" />
		</div>
	);
}

// Error names what failed and offers the one action that recovers it: retry.
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
	return (
		<Reveal index={0}>
			<Card title="Cannot load the dashboard">
				<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.lg }}>
					<span style={{ color: tokens.color.text.secondary }}>{message}</span>
					<div>
						<Button variant="primary" accent="blue" onClick={onRetry}>
							Retry
						</Button>
					</div>
				</div>
			</Card>
		</Reveal>
	);
}

// A signed in principal without the admin role. Names the constraint and offers to sign out.
function ForbiddenState() {
	const { logout } = useAuth();
	return (
		<Reveal index={0}>
			<Card title="Admin access required">
				<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.lg }}>
					<span style={{ color: tokens.color.text.secondary }}>
						This dashboard is limited to admin accounts. Sign in with an admin account to continue.
					</span>
					<div>
						<Button variant="default" onClick={() => void logout()}>
							Sign out
						</Button>
					</div>
				</div>
			</Card>
		</Reveal>
	);
}

// Content. Health first (the one thing a glance is counting on), then the measured counts, then the
// module record. Controls a viewer came to act on arrive early in the entrance sequence.
function ReadyState({ overview, health }: { overview: AdminOverview; health: HealthReport }) {
	const components = Object.entries(health.components ?? {});
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xl }}>
			<Reveal index={0}>
				<Card
					title="Service health"
					action={<StatusPill label={health.status} accent={healthAccent(health.status)} pulse={health.status !== "UP"} />}
				>
					{components.length === 0 ? (
						<span style={{ color: tokens.color.text.muted }}>No component details available.</span>
					) : (
						<ul style={listStyle}>
							{components.map(([name, c], i) => (
								<li key={name} style={rowStyle(i === 0)}>
									<span style={{ color: tokens.color.text.primary }}>{name}</span>
									<StatusPill label={c.status} accent={healthAccent(c.status)} />
								</li>
							))}
						</ul>
					)}
				</Card>
			</Reveal>

			<Reveal index={1}>
				<div style={gridStyle}>
					<StatTile label="Seed / key pairs" value={overview.audioReflash.pairCount} />
					<StatTile label="Users" value={overview.users.count} />
				</div>
			</Reveal>

			<Reveal index={2}>
				<Card title="Modules">
					{overview.modules.length === 0 ? (
						// Empty names the action that fills it.
						<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
							<span style={{ color: tokens.color.text.muted }}>
								No modules reported. Confirm the service started cleanly, then reload.
							</span>
						</div>
					) : (
						<ul style={listStyle}>
							{overview.modules.map((name, i) => (
								<li key={name} style={rowStyle(i === 0)}>
									<Label tone="primary">{name}</Label>
								</li>
							))}
						</ul>
					)}
				</Card>
			</Reveal>

			{overview.buildInfo && (overview.buildInfo.name || overview.buildInfo.version) && (
				<Reveal index={3}>
					<span style={{ ...tokens.type.microcap, color: tokens.color.text.muted }}>
						{[overview.buildInfo.name, overview.buildInfo.version].filter(Boolean).join(" ")}
					</span>
				</Reveal>
			)}
		</div>
	);
}

// Responsive tile grid: columns collapse to one under the tile minimum width.
const gridStyle = {
	display: "grid",
	gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.layout.tileMin}px, 1fr))`,
	gap: tokens.space.xl,
} as const;

const listStyle = { listStyle: "none", margin: 0, padding: 0 } as const;

// One row of a single surface list, hairline separated from the row above.
function rowStyle(first: boolean) {
	return {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: tokens.space.md,
		paddingBlock: tokens.space.md,
		borderTop: first ? undefined : hairline(tokens.color.stroke.divider),
	} as const;
}
